// src/pages/TrackCreation/Step1.tsx
import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Button from "../ui/Button";
import shell from "../../pages/track/create/createShell.module.css";

interface Subcategory {
    name: string;
    step: string;
}

interface SidebarItem {
    title: string;
    step?: string;
    hasSubcategories: boolean;
    subcategories?: Subcategory[];
}

const MuseumStoriesSidebar: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    // Replace the current "story-*" segment with the new step.
    const replaceStepInUrl = (newStep: string): string => {
        return location.pathname.replace(/story-[^/]+/, newStep);
    };

    // Extract a value from the URL using a regex pattern.
    // Example: matches URLs like "/create/tour/.../story-.../.../.../<value>"
    const extractStoryId = (): string | null => {
        const match = location.pathname.match(/story-[^/]+\/([^/]+)/);
        return match ? match[1] : null;
    };

    const extractValueFromPattern = (): string | null => {
        const regex = /^\/create\/tour\/[^/]+\/story-[^/]+\/[^/]+\/[^/]+\/([^/]+)$/;
        const match = location.pathname.match(regex);
        return match ? match[1] : null;
    };

    // Determine if extra subcategories should be displayed.
    const additionalSubcategories = extractValueFromPattern() !== null;

    const sidebarItems: SidebarItem[] = [
        {
            title: t("storyDetails"),
            hasSubcategories: true,
            subcategories: [
                { name: t("language"), step: "story-language" },
                ...(additionalSubcategories
                    ? [
                          { name: t("title"), step: "story-title" },
                          { name: t("localizationStory"), step: "story-zone" },
                          { name: t("text"), step: "story-text" },
                          { name: t("audio"), step: "story-audio" },
                          { name: t("photos"), step: "story-photos" },
                      ]
                    : []),
            ],
        },
    ];

    return (
        <div
            className={`track-create-sidebar-navigation experience-story-sidebar ${shell.navRoot} ${shell.navStoryRoot}`}
        >
            <ul className={`track-create-sidebar-nav ${shell.navList}`}>
                {sidebarItems.map((item, index) => (
                    <div key={index} className={shell.navGroup}>
                        {!item.hasSubcategories ? (
                            <li
                                className={`sidebar-title ${shell.navTitleLink} ${
                                    item.step && location.pathname.includes(item.step) ? shell.navTitleLinkActive : ""
                                }`}
                            >
                                <Link to={replaceStepInUrl(item.step || "")} className="sidebar-link">
                                    {item.title}
                                </Link>
                            </li>
                        ) : (
                            <>
                                <li className={`sidebar-title sidebar-group-title ${shell.navHeading}`}>
                                    {item.title}
                                </li>
                                <ul className={`subcategory-list ${shell.navSubList}`}>
                                    {item.subcategories?.map((sub, subIndex) => (
                                        <li
                                            key={subIndex}
                                            className={`${shell.navSubItem} ${
                                                location.pathname.includes(sub.step) ? shell.navSubItemActive : ""
                                            }`}
                                        >
                                            <Link to={replaceStepInUrl(sub.step)} className="sidebar-link">
                                                {sub.name}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </div>
                ))}
            </ul>
            <Button
                label={t("back")}
                variant="outline"
                shape="pill"
                className={shell.navBackBtn}
                onClick={() => {
                    navigate(location.pathname.replace(/\/story-[^/]+\/.+$/, "/museum-stories"), {
                        state: { action: extractStoryId() },
                    });
                }}
            />
        </div>
    );
};

export default MuseumStoriesSidebar;
